import React, { useState, useEffect, useCallback } from 'react';
import { GlassCard } from '../ui/GlassCard';
import { GlassButton } from '../ui/GlassButton';
import { GlassInput } from '../ui/GlassInput';
import { apiClient } from '../../services/api';
import {
  Key,
  Shield,
  Users,
  BarChart2,
  Plus,
  AlertTriangle,
  Check,
  X,
  ChevronDown,
  RefreshCw,
  TrendingUp,
  DollarSign,
  Layers,
} from 'lucide-react';
import './APIPoolsAdmin.css';

/** Normalize FastAPI error detail — may be a string or a Pydantic validation error array */
const getApiError = (e: any, fallback: string): string => {
  const detail = e?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((d: any) => d?.msg ?? JSON.stringify(d)).join('; ');
  }
  return fallback;
};


// ── Types ─────────────────────────────────────────────────────────────────────

interface APIPool {
  id: number;
  pool_name: string;
  status: 'active' | 'suspended' | 'full';
  max_users: number;
  current_users: number;
  monthly_budget: number;
  monthly_usage: number;
  has_key: boolean;
  created_at: string | null;
}

interface PoolSummary {
  total_pools: number;
  active_pools: number;
  total_capacity: number;
  total_users: number;
  total_monthly_budget: number;
  total_monthly_spend: number;
  pools_near_capacity: number;
}

interface UsageByModel {
  model: string;
  tokens: number;
  cost: number;
}

interface UsageData {
  total_tokens: number;
  total_cost: number;
  days: number;
  by_model: UsageByModel[];
  daily: { date: string; tokens: number; cost: number }[];
}

interface PoolUser {
  user_id: string;
  username: string;
  email: string;
  monthly_token_used: number;
  monthly_token_limit: number;
  total_requests: number;
  status: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtCurrency(val: number): string {
  return '$' + (val ?? 0).toFixed(2);
}

function pct(used: number, max: number): number {
  if (max === 0) return 0;
  return Math.min(100, Math.round((used / max) * 100));
}

function poolStatus(pool: APIPool): 'active' | 'suspended' | 'full' {
  if (pool.status === 'suspended') return 'suspended';
  if (pool.current_users >= pool.max_users) return 'full';
  return pool.status;
}

// ── Sub-components ────────────────────────────────────────────────────────────

interface MiniBarProps {
  used: number;
  max: number;
  warn?: boolean;
  className?: string;
}
const MiniBar: React.FC<MiniBarProps> = ({ used, max, warn, className }) => {
  const p = pct(used, max);
  return (
    <div className={`mini-bar-track ${className || ''}`}>
      <div
        className={`mini-bar-fill ${warn && p >= 90 ? 'mini-bar-warn' : ''} ${p >= 100 ? 'mini-bar-full' : ''}`}
        style={{ width: `${p}%` }}
      />
    </div>
  );
};

interface StatCardProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  sub?: string;
  warn?: boolean;
  barUsed?: number;
  barMax?: number;
}
const StatCard: React.FC<StatCardProps> = ({ icon, label, value, sub, warn, barUsed, barMax }) => (
  <GlassCard padding="md" className="pool-stat-card">
    <div className="pool-stat-icon">{icon}</div>
    <div className="pool-stat-label">{label}</div>
    <div className={`pool-stat-value ${warn ? 'pool-stat-warn' : ''}`}>{value}</div>
    {sub && <div className="pool-stat-sub">{sub}</div>}
    {barUsed !== undefined && barMax !== undefined && (
      <MiniBar used={barUsed} max={barMax} warn={warn} className="pool-stat-bar" />
    )}
  </GlassCard>
);

// ── Main Component ────────────────────────────────────────────────────────────

const APIPoolsAdmin: React.FC = () => {
  const [pools, setPools] = useState<APIPool[]>([]);
  const [summary, setSummary] = useState<PoolSummary | null>(null);
  const [usage, setUsage] = useState<UsageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Per-pool expanded row state (shows users)
  const [expandedPoolId, setExpandedPoolId] = useState<number | null>(null);
  const [poolUsers, setPoolUsers] = useState<Record<number, PoolUser[]>>({});
  const [poolUsersLoading, setPoolUsersLoading] = useState<Record<number, boolean>>({});

  // Add pool form
  const [showAddForm, setShowAddForm] = useState(false);
  const [newPool, setNewPool] = useState({
    pool_name: '',
    api_key: '',
    max_users: '20',
    monthly_budget: '60',
  });
  const [addLoading, setAddLoading] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const [addSuccess, setAddSuccess] = useState(false);

  // Patch status loading
  const [patchLoading, setPatchLoading] = useState<Record<number, boolean>>({});
  const [patchError, setPatchError] = useState<Record<number, string | null>>({});

  // ── Load ──────────────────────────────────────────────────────────────────

  const loadAll = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [poolsRes, summaryRes, usageRes] = await Promise.all([
        apiClient.get('/admin/api-pools'),
        apiClient.get('/admin/api-pools/summary'),
        apiClient.get('/admin/api-usage?days=30'),
      ]);
      setPools(poolsRes.data);
      setSummary(summaryRes.data);
      setUsage(usageRes.data);
    } catch (err: any) {
      setLoadError(getApiError(err, 'Failed to load API pool data'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // ── Actions ───────────────────────────────────────────────────────────────

  const handleToggleExpand = async (poolId: number) => {
    if (expandedPoolId === poolId) {
      setExpandedPoolId(null);
      return;
    }
    setExpandedPoolId(poolId);
    if (!poolUsers[poolId]) {
      setPoolUsersLoading(prev => ({ ...prev, [poolId]: true }));
      try {
        const res = await apiClient.get(`/admin/api-pools/${poolId}/users`);
        setPoolUsers(prev => ({ ...prev, [poolId]: res.data }));
      } catch {
        setPoolUsers(prev => ({ ...prev, [poolId]: [] }));
      } finally {
        setPoolUsersLoading(prev => ({ ...prev, [poolId]: false }));
      }
    }
  };

  const handlePatchStatus = async (pool: APIPool, newStatus: 'active' | 'suspended') => {
    setPatchLoading(prev => ({ ...prev, [pool.id]: true }));
    setPatchError(prev => ({ ...prev, [pool.id]: null }));
    try {
      await apiClient.patch(`/admin/api-pools/${pool.id}`, { status: newStatus });
      setPools(prev => prev.map(p => p.id === pool.id ? { ...p, status: newStatus } : p));
    } catch (err: any) {
      setPatchError(prev => ({
        ...prev,
        [pool.id]: getApiError(err, 'Update failed'),
      }));
    } finally {
      setPatchLoading(prev => ({ ...prev, [pool.id]: false }));
    }
  };

  const handleAddPool = async (e: React.FormEvent) => {
    e.preventDefault();
    setAddLoading(true);
    setAddError(null);
    setAddSuccess(false);
    try {
      await apiClient.post('/admin/api-pools', {
        pool_name: newPool.pool_name.trim(),
        api_key: newPool.api_key,
        max_users: parseInt(newPool.max_users, 10),
        monthly_budget: parseFloat(newPool.monthly_budget),
      });
      setAddSuccess(true);
      setNewPool({ pool_name: '', api_key: '', max_users: '20', monthly_budget: '60' });
      setShowAddForm(false);
      loadAll();
    } catch (err: any) {
      setAddError(getApiError(err, 'Failed to create pool'));
    } finally {
      setAddLoading(false);
    }
  };

  // Auto-suggest next pool name
  const suggestPoolName = () => {
    if (pools.length === 0) return 'Pool 01';
    const nums = pools.map(p => {
      const m = p.pool_name.match(/\d+$/);
      return m ? parseInt(m[0], 10) : 0;
    });
    const next = Math.max(...nums) + 1;
    return `Pool ${String(next).padStart(2, '0')}`;
  };

  const handleOpenAddForm = () => {
    setShowAddForm(true);
    setAddError(null);
    setAddSuccess(false);
    setNewPool(prev => ({
      ...prev,
      pool_name: prev.pool_name || suggestPoolName(),
    }));
  };

  // ── Render: loading / error ───────────────────────────────────────────────

  if (loading) {
    return (
      <GlassCard padding="lg">
        <p style={{ color: 'var(--text-secondary)' }}>Loading API pool data...</p>
      </GlassCard>
    );
  }

  if (loadError) {
    return (
      <GlassCard padding="lg">
        <p style={{ color: 'var(--danger)' }}>{loadError}</p>
        <GlassButton variant="secondary" onClick={loadAll} icon={<RefreshCw size={16} />} style={{ marginTop: '1rem' }}>
          Retry
        </GlassButton>
      </GlassCard>
    );
  }

  // ── Render: usage chart helpers ───────────────────────────────────────────

  const maxModelTokens = usage
    ? Math.max(...(usage.by_model.map(m => m.tokens)), 1)
    : 1;

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="api-pools-admin">

      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div className="api-pools-header">
        <h3 className="api-pools-title">
          <Shield size={20} style={{ verticalAlign: 'middle', marginRight: '0.5rem' }} />
          Gemini API Pool Management
        </h3>
        <p className="api-pools-subtitle">
          Monitor capacity, spend, and status for all Gemini API key pools. Keys are encrypted at rest.
        </p>
        <div className="api-pools-header-actions">
          <GlassButton variant="secondary" size="sm" onClick={loadAll} icon={<RefreshCw size={14} />}>
            Refresh
          </GlassButton>
          <GlassButton
            variant="primary"
            size="sm"
            onClick={handleOpenAddForm}
            icon={<Plus size={14} />}
          >
            Add Pool
          </GlassButton>
        </div>
      </div>

      {/* ── Summary Stats ──────────────────────────────────────────────── */}
      {summary && (
        <div className="pool-stats-row">
          <StatCard
            icon={<Layers size={20} />}
            label="Active Pools"
            value={`${summary.active_pools} of ${summary.total_pools}`}
            sub="pools provisioned"
            barUsed={summary.active_pools}
            barMax={summary.total_pools}
          />
          <StatCard
            icon={<Users size={20} />}
            label="Users Assigned"
            value={`${summary.total_users} / ${summary.total_capacity}`}
            sub="total capacity"
            barUsed={summary.total_users}
            barMax={summary.total_capacity}
            warn={pct(summary.total_users, summary.total_capacity) >= 90}
          />
          <StatCard
            icon={<DollarSign size={20} />}
            label="Monthly Spend"
            value={fmtCurrency(summary.total_monthly_spend)}
            sub={`of ${fmtCurrency(summary.total_monthly_budget)} budget`}
            barUsed={summary.total_monthly_spend}
            barMax={summary.total_monthly_budget}
            warn={pct(summary.total_monthly_spend, summary.total_monthly_budget) >= 90}
          />
          <StatCard
            icon={<AlertTriangle size={20} />}
            label="Pools Near Capacity"
            value={String(summary.pools_near_capacity)}
            sub="> 90% user slots filled"
            warn={summary.pools_near_capacity > 0}
          />
        </div>
      )}

      {/* ── Add Pool Form (collapsible) ─────────────────────────────────── */}
      {showAddForm && (
        <GlassCard padding="lg" className="add-pool-card">
          <div className="add-pool-header">
            <h4 className="add-pool-title">
              <Key size={16} style={{ marginRight: '0.4rem', verticalAlign: 'middle' }} />
              Add New Pool
            </h4>
            <GlassButton
              variant="ghost"
              size="sm"
              onClick={() => setShowAddForm(false)}
              icon={<X size={16} />}
              title="Cancel"
            />
          </div>

          <form onSubmit={handleAddPool} className="add-pool-form">
            <div className="add-pool-fields">
              <GlassInput
                label="Pool Name"
                value={newPool.pool_name}
                onChange={v => setNewPool(prev => ({ ...prev, pool_name: v }))}
                placeholder="e.g. Pool 01"
                required
              />
              <GlassInput
                label="API Key"
                type="password"
                value={newPool.api_key}
                onChange={v => setNewPool(prev => ({ ...prev, api_key: v }))}
                placeholder="AIza..."
                required
              />
              <GlassInput
                label="Max Users"
                type="number"
                value={newPool.max_users}
                onChange={v => setNewPool(prev => ({ ...prev, max_users: v }))}
                placeholder="20"
                required
              />
              <GlassInput
                label="Monthly Budget ($)"
                type="number"
                value={newPool.monthly_budget}
                onChange={v => setNewPool(prev => ({ ...prev, monthly_budget: v }))}
                placeholder="60"
                required
              />
            </div>

            {addError && (
              <div className="add-pool-error">
                <X size={14} /> {addError}
              </div>
            )}

            <div className="add-pool-actions">
              <GlassButton
                variant="secondary"
                type="button"
                onClick={() => setShowAddForm(false)}
              >
                Cancel
              </GlassButton>
              <GlassButton
                variant="primary"
                type="submit"
                disabled={addLoading}
                icon={addLoading ? <RefreshCw size={14} className="spin" /> : <Plus size={14} />}
              >
                {addLoading ? 'Creating...' : 'Create Pool'}
              </GlassButton>
            </div>
          </form>
        </GlassCard>
      )}

      {addSuccess && (
        <div className="pool-success-banner">
          <Check size={14} /> Pool created successfully.
        </div>
      )}

      {/* ── Pool Table ──────────────────────────────────────────────────── */}
      <GlassCard padding="none" className="pool-table-card">
        <div className="pool-table-header-row">
          <h4 className="pool-table-title">All Pools</h4>
          <span className="pool-table-count">{pools.length} pool{pools.length !== 1 ? 's' : ''}</span>
        </div>

        {pools.length === 0 ? (
          <div className="pool-table-empty">
            No API pools configured. Add a pool to get started.
          </div>
        ) : (
          <div className="pool-table-wrap">
            <table className="pool-table">
              <thead>
                <tr>
                  <th>Pool</th>
                  <th>Users</th>
                  <th>Monthly Spend</th>
                  <th>Status</th>
                  <th>Key</th>
                  <th>Actions</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {pools.map(pool => {
                  const status = poolStatus(pool);
                  const userPct = pct(pool.current_users, pool.max_users);
                  const spendPct = pct(pool.monthly_usage, pool.monthly_budget);
                  const isExpanded = expandedPoolId === pool.id;
                  const err = patchError[pool.id];

                  return (
                    <React.Fragment key={pool.id}>
                      <tr className={`pool-row ${isExpanded ? 'pool-row-expanded' : ''}`}>
                        {/* Pool name */}
                        <td className="pool-cell-name">{pool.pool_name}</td>

                        {/* Users bar */}
                        <td className="pool-cell-users">
                          <span className="pool-cell-label">
                            {pool.current_users} / {pool.max_users}
                          </span>
                          <MiniBar
                            used={pool.current_users}
                            max={pool.max_users}
                            warn={userPct >= 90}
                          />
                        </td>

                        {/* Spend bar */}
                        <td className="pool-cell-spend">
                          <span className="pool-cell-label">
                            {fmtCurrency(pool.monthly_usage)} / {fmtCurrency(pool.monthly_budget)}
                          </span>
                          <MiniBar
                            used={pool.monthly_usage}
                            max={pool.monthly_budget}
                            warn={spendPct >= 90}
                          />
                        </td>

                        {/* Status chip */}
                        <td>
                          <span className={`pool-status-chip pool-status-${status}`}>
                            {status === 'active' && <Check size={11} />}
                            {status === 'suspended' && <X size={11} />}
                            {status === 'full' && <AlertTriangle size={11} />}
                            {status.charAt(0).toUpperCase() + status.slice(1)}
                          </span>
                        </td>

                        {/* Key indicator */}
                        <td>
                          {pool.has_key ? (
                            <span className="pool-key-ok" title="API key provisioned">
                              <Key size={14} />
                            </span>
                          ) : (
                            <span className="pool-key-missing" title="No API key set">
                              <AlertTriangle size={14} />
                            </span>
                          )}
                        </td>

                        {/* Actions */}
                        <td className="pool-cell-actions">
                          {err && (
                            <span className="pool-patch-error" title={err}>
                              <AlertTriangle size={12} />
                            </span>
                          )}
                          {status !== 'suspended' ? (
                            <GlassButton
                              variant="secondary"
                              size="sm"
                              disabled={patchLoading[pool.id]}
                              onClick={() => handlePatchStatus(pool, 'suspended')}
                              icon={patchLoading[pool.id] ? <RefreshCw size={12} className="spin" /> : <X size={12} />}
                            >
                              Suspend
                            </GlassButton>
                          ) : (
                            <GlassButton
                              variant="primary"
                              size="sm"
                              disabled={patchLoading[pool.id]}
                              onClick={() => handlePatchStatus(pool, 'active')}
                              icon={patchLoading[pool.id] ? <RefreshCw size={12} className="spin" /> : <Check size={12} />}
                            >
                              Activate
                            </GlassButton>
                          )}
                        </td>

                        {/* Expand toggle */}
                        <td className="pool-cell-expand">
                          <button
                            className={`pool-expand-btn ${isExpanded ? 'pool-expand-btn-open' : ''}`}
                            onClick={() => handleToggleExpand(pool.id)}
                            title={isExpanded ? 'Collapse users' : 'Expand users'}
                          >
                            <ChevronDown size={16} />
                          </button>
                        </td>
                      </tr>

                      {/* Expanded users sub-row */}
                      {isExpanded && (
                        <tr className="pool-users-row">
                          <td colSpan={7} className="pool-users-cell">
                            {poolUsersLoading[pool.id] ? (
                              <p className="pool-users-loading">Loading users...</p>
                            ) : !poolUsers[pool.id] || poolUsers[pool.id].length === 0 ? (
                              <p className="pool-users-empty">No users assigned to this pool.</p>
                            ) : (
                              <table className="pool-users-table">
                                <thead>
                                  <tr>
                                    <th>Username</th>
                                    <th>Email</th>
                                    <th>Tokens Used (mo.)</th>
                                    <th>Requests</th>
                                    <th>Key Status</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {poolUsers[pool.id].map(u => {
                                    const tokPct = pct(u.monthly_token_used, u.monthly_token_limit);
                                    return (
                                      <tr key={u.user_id}>
                                        <td className="pool-users-mono">{u.username}</td>
                                        <td className="pool-users-secondary">{u.email}</td>
                                        <td>
                                          <span className={`pool-users-tokens ${tokPct >= 90 ? 'pool-users-tokens-warn' : ''}`}>
                                            {u.monthly_token_used.toLocaleString()} / {u.monthly_token_limit.toLocaleString()}
                                          </span>
                                          <MiniBar
                                            used={u.monthly_token_used}
                                            max={u.monthly_token_limit}
                                            warn={tokPct >= 90}
                                          />
                                        </td>
                                        <td className="pool-users-secondary">{u.total_requests.toLocaleString()}</td>
                                        <td>
                                          <span className={`pool-users-status pool-users-status-${u.status}`}>
                                            {u.status}
                                          </span>
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </GlassCard>

      {/* ── Usage Chart (last 30 days) ──────────────────────────────────── */}
      {usage && (
        <GlassCard padding="lg" className="usage-chart-card">
          <div className="usage-chart-header">
            <h4 className="usage-chart-title">
              <BarChart2 size={16} style={{ marginRight: '0.4rem', verticalAlign: 'middle' }} />
              Usage — Last {usage.days} Days
            </h4>
            <div className="usage-chart-totals">
              <span className="usage-total-item">
                <TrendingUp size={13} />
                {usage.total_tokens.toLocaleString()} tokens
              </span>
              <span className="usage-total-item">
                <DollarSign size={13} />
                {fmtCurrency(usage.total_cost)} cost
              </span>
            </div>
          </div>

          {/* By-model breakdown */}
          {usage.by_model.length === 0 ? (
            <p className="usage-chart-empty">No usage data recorded yet.</p>
          ) : (
            <div className="usage-model-breakdown">
              <p className="usage-model-label">Breakdown by model</p>
              {usage.by_model.map(m => {
                const barPct = Math.round((m.tokens / maxModelTokens) * 100);
                return (
                  <div key={m.model} className="usage-model-row">
                    <span className="usage-model-name">{m.model}</span>
                    <div className="usage-model-bar-wrap">
                      <div
                        className="usage-model-bar"
                        style={{ width: `${barPct}%` }}
                      />
                    </div>
                    <span className="usage-model-tokens">
                      {m.tokens.toLocaleString()} tokens
                    </span>
                    <span className="usage-model-cost">
                      {fmtCurrency(m.cost)}
                    </span>
                    <span className="usage-model-pct">
                      {usage.total_tokens > 0
                        ? Math.round((m.tokens / usage.total_tokens) * 100)
                        : 0}%
                    </span>
                  </div>
                );
              })}
            </div>
          )}

          {/* Daily bars — simple sparkline */}
          {usage.daily && usage.daily.length > 0 && (
            <div className="usage-daily-section">
              <p className="usage-model-label">Daily token volume</p>
              <div className="usage-daily-bars">
                {usage.daily.map(day => {
                  const maxDay = Math.max(...usage.daily.map(d => d.tokens), 1);
                  const h = Math.max(4, Math.round((day.tokens / maxDay) * 48));
                  return (
                    <div key={day.date} className="usage-daily-col" title={`${day.date}: ${day.tokens.toLocaleString()} tokens, ${fmtCurrency(day.cost)}`}>
                      <div className="usage-daily-bar" style={{ height: `${h}px` }} />
                      <span className="usage-daily-date">
                        {new Date(day.date).toLocaleDateString(undefined, { month: 'numeric', day: 'numeric' })}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </GlassCard>
      )}
    </div>
  );
};

export default APIPoolsAdmin;
